import React, { useState } from 'react';
import { TPlan, TFeature } from '../../services/types/utils.types';
import { tWithKeyAttr as t } from '../../i18nHelper';
import styles from './planCard.module.css';

interface PlanCardProps {
  plan: TPlan;
  isSelected?: boolean;
  onSelect?: () => void;
  showGetStartedButton?: boolean;
  onGetStarted?: () => void;
  allFeatures?: TFeature[];
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isSelected = false,
  onSelect,
  showGetStartedButton = true,
  onGetStarted,
  allFeatures = []
}) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const handleCardClick = () => {
    if (onSelect) {
      onSelect();
    }
  };

  const handleGetStartedClick = (e: React.MouseEvent) => {
    if (onGetStarted) {
      onGetStarted();
    }
  };

  const handleSeeAllClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAllFeatures(!showAllFeatures);
  };


  const includedFeatureIds = plan.features?.map(f => f.id) || [];

  const featuresToShow = allFeatures.length > 0
    ? showAllFeatures
      ? allFeatures
      : allFeatures.slice(0, 3)
    : [];

  const hasMoreFeatures = allFeatures.length > 3;

  // Check if a feature is included in the plan
  const isFeatureIncluded = (featureId: number) => {
    return includedFeatureIds.includes(featureId);
  };

  return (
    <article
      className={`${styles.pricingCard} ${styles.fadeIn} ${isSelected ? styles.selected : ''}`}
      onClick={handleCardClick}
      style={{ animationDelay: '0.1s' }}
    >
      <header className={styles.planName}>{plan.name}</header>
      <section className={styles.planDescription}>{plan.description}</section>
      <section className={styles.planPrice}>{plan.currency}{plan.monthlyPrice ?? plan.monthlyPrice}</section>
      <section className={styles.planPeriod}>{t("PER_MONTH")}*</section>

      <ul className={styles.planFeatures}>
        {allFeatures.length > 0 ? (
          featuresToShow.map((feature) => {
            const isIncluded = isFeatureIncluded(feature.id);
            return (
              <li
                key={feature.id}
                className={isIncluded ? styles.included : styles.excluded}
              >
                {isIncluded ? (
                  <span className={styles.checkIcon}></span>
                ) : (
                  <span className={styles.timesIcon}></span>
                )}
                {feature.desc}
              </li>
            );
          })
        ) : (
          <>
            <li className={styles.included}><span className={styles.checkIcon}></span>Session documentation & summaries</li>
            <li className={styles.included}><span className={styles.checkIcon}></span>Smart appointment scheduling</li>
            <li className={styles.included}><span className={styles.checkIcon}></span>Video therapy sessions</li>
            <li className={styles.included}><span className={styles.checkIcon}></span>Centralized data management</li>
            <li className={styles.included}><span className={styles.checkIcon}></span>Patient chat system</li>
            <li className={styles.included}><span className={styles.checkIcon}></span>Up to 20 patients</li>
            <li className={styles.excluded}><span className={styles.timesIcon}></span>AI chatbot intake</li>
            <li className={styles.excluded}><span className={styles.timesIcon}></span>Advanced clinical tracking</li>
            <li className={styles.excluded}><span className={styles.timesIcon}></span>Emergency alerts</li>
          </>
        )}

        {/* See All button - only show if there are more than 3 features */}
        {hasMoreFeatures && (
          <li className={styles.seeAllButton} onClick={handleSeeAllClick}>
            {showAllFeatures ? t("SHOW_LESS") : t("SEE_ALL")}
          </li>
        )}
      </ul>

      {/* Choose Button - only show for non-selected plans */}
      {!isSelected && onSelect && (
        <button
          className={`${styles.planCta}`}
          onClick={handleGetStartedClick}
        >
          {t("CHOOSE")}
        </button>
      )}

      {/* Get Started Button - only show when explicitly requested */}
      {showGetStartedButton && isSelected && (
        <button
          className={styles.planCta}
          onClick={handleGetStartedClick}
        >
          Get Started
        </button>
      )}

      {/* Checkmark icon - only show for selected plan */}
      {isSelected && (
        <div className={styles.checkmark}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
              fill="#4CAF50"
            />
          </svg>
        </div>
      )}
    </article>
  );
};

export default PlanCard;
